import type { ComboboxItem } from '@/components'
import { Combobox, DatePicker, FAIcon, TimePicker } from '@/components'
import { Button, Checkbox, Input } from '@/components/ui'
import { addDays, set } from 'date-fns'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useMemo } from 'react'
import type { EventNode, EventQuestionType } from '../types.graphql'

export interface EventQuestionDraft {
  /** Stable key for the list; a question saved earlier also carries its id. */
  key: string
  id?: string
  label: string
  type: EventQuestionType
  required: boolean
}

export interface EventFormValues {
  startsAt: Date
  endsAt: Date | null
  location: string
  registrationDeadline: Date | null
  questions: EventQuestionDraft[]
}

export type EventFormError =
  | 'ends-before-start'
  | 'deadline-after-start'
  | 'question-without-label'
  | null

const atHour = (date: Date, hours: number) =>
  set(date, { hours, minutes: 0, seconds: 0, milliseconds: 0 })

let questionKey = 0
const nextKey = () => `question-${(questionKey += 1)}`

export const newQuestion = (
  label = '',
  type: EventQuestionType = 'YES_NO'
): EventQuestionDraft => ({ key: nextKey(), label, type, required: false })

export const defaultEventFormValues = (): EventFormValues => ({
  startsAt: atHour(addDays(new Date(), 14), 18),
  endsAt: null,
  location: '',
  registrationDeadline: null,
  questions: [],
})

/** The form as it looks for an event that already exists. */
export const eventFormValuesFromEvent = (
  event: EventNode
): EventFormValues => ({
  startsAt: new Date(event.startsAt),
  endsAt: event.endsAt ? new Date(event.endsAt) : null,
  location: event.location,
  registrationDeadline: event.registrationDeadline
    ? new Date(event.registrationDeadline)
    : null,
  questions: event.questions.map(question => ({
    key: question.id,
    id: question.id,
    label: question.label,
    type: question.type,
    required: question.required,
  })),
})

export function validateEventForm(values: EventFormValues): EventFormError {
  if (values.endsAt && values.endsAt < values.startsAt)
    return 'ends-before-start'
  if (
    values.registrationDeadline &&
    values.registrationDeadline > values.startsAt
  )
    return 'deadline-after-start'
  if (values.questions.some(question => !question.label.trim()))
    return 'question-without-label'
  return null
}

/** Replace the calendar day of `base` with `picked`, keeping `base`'s time. */
const withDay = (picked: Date, base: Date) =>
  set(picked, {
    year: picked.getFullYear(),
    month: picked.getMonth(),
    date: picked.getDate(),
    hours: base.getHours(),
    minutes: base.getMinutes(),
    seconds: 0,
    milliseconds: 0,
  })

const fieldLabel = 'text-xs font-semibold uppercase text-stone-500'

interface DateTimeFieldProps {
  label: string
  value: Date | null
  /** Used as the starting point when an empty optional field is filled in. */
  fallback: Date
  clearable?: boolean
  onChange(value: Date | null): void
}

const DateTimeField: React.FC<DateTimeFieldProps> = ({
  label,
  value,
  fallback,
  clearable = false,
  onChange,
}) => {
  const translations = useTranslate({
    add: 'common.add',
    remove: 'common.remove',
  })

  return (
    <div className="flex flex-col gap-1">
      <span className={fieldLabel}>{label}</span>

      {value ? (
        <div className="flex flex-wrap items-center gap-2">
          <TimePicker value={value} onChange={onChange} />

          <DatePicker
            mode="single"
            value={value}
            onChange={date =>
              date ? onChange(withDay(date, value)) : undefined
            }
          />

          {clearable && (
            <button
              type="button"
              className="text-stone-400 hover:text-stone-600"
              title={translations.remove}
              onClick={() => onChange(null)}
            >
              <FAIcon icon="xmark" />
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="h-9 self-start text-sm font-medium text-[#003057] hover:underline"
          onClick={() => onChange(fallback)}
        >
          {translations.add}
        </button>
      )}
    </div>
  )
}

interface Props {
  values: EventFormValues
  onChange(values: EventFormValues): void
}

export const EventFormFields: React.FC<Props> = ({ values, onChange }) => {
  const translations = useTranslate({
    startsAt: 'events.starts-at',
    endsAt: 'events.ends-at',
    location: 'common.location',
    locationPlaceholder: 'events.location-placeholder',
    deadline: 'events.registration-deadline',
    deadlineHelp: 'events.registration-deadline-help',
    questions: 'events.questions',
    questionsHelp: 'events.questions-help',
    questionLabel: 'events.question-label-placeholder',
    addQuestion: 'events.add-question',
    presetCompanion: 'events.preset-companion',
    presetOvernight: 'events.preset-overnight',
    required: 'events.question-required',
    remove: 'common.remove',
    typeYesNo: 'events.question-type-yes-no',
    typeText: 'events.question-type-text',
    typeNumber: 'events.question-type-number',
    endsBeforeStart: 'events.error-ends-before-start',
    deadlineAfterStart: 'events.error-deadline-after-start',
    questionWithoutLabel: 'events.error-question-without-label',
  })

  const update = (patch: Partial<EventFormValues>) =>
    onChange({ ...values, ...patch })

  const updateQuestion = (key: string, patch: Partial<EventQuestionDraft>) =>
    update({
      questions: values.questions.map(question =>
        question.key === key ? { ...question, ...patch } : question
      ),
    })

  const addQuestion = (question: EventQuestionDraft) =>
    update({ questions: [...values.questions, question] })

  const typeItems = useMemo<ComboboxItem<EventQuestionType>[]>(
    () => [
      { value: 'YES_NO', label: translations.typeYesNo },
      { value: 'TEXT', label: translations.typeText },
      { value: 'NUMBER', label: translations.typeNumber },
    ],
    [translations.typeYesNo, translations.typeText, translations.typeNumber]
  )

  const error = validateEventForm(values)
  const errorText = {
    'ends-before-start': translations.endsBeforeStart,
    'deadline-after-start': translations.deadlineAfterStart,
    'question-without-label': translations.questionWithoutLabel,
  }

  const hasPreset = (label: string) =>
    values.questions.some(question => question.label === label)

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-x-10 gap-y-4">
        <DateTimeField
          label={translations.startsAt}
          value={values.startsAt}
          fallback={values.startsAt}
          onChange={startsAt => startsAt && update({ startsAt })}
        />

        <DateTimeField
          label={translations.endsAt}
          value={values.endsAt}
          fallback={set(values.startsAt, {
            hours: values.startsAt.getHours() + 4,
          })}
          clearable
          onChange={endsAt => update({ endsAt })}
        />
      </div>

      <div className="flex flex-wrap gap-x-10 gap-y-4">
        <div className="flex flex-col gap-1">
          <span className={fieldLabel}>{translations.location}</span>
          <Input
            value={values.location}
            placeholder={translations.locationPlaceholder}
            className="w-72"
            onChange={location => update({ location })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <DateTimeField
            label={translations.deadline}
            value={values.registrationDeadline}
            fallback={addDays(values.startsAt, -7)}
            clearable
            onChange={registrationDeadline => update({ registrationDeadline })}
          />
          <span className="text-xs text-stone-500">
            {translations.deadlineHelp}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-2 border-t border-stone-200 pt-4">
        <div className="flex flex-col gap-0.5">
          <span className={fieldLabel}>{translations.questions}</span>
          <span className="text-xs text-stone-500">
            {translations.questionsHelp}
          </span>
        </div>

        {values.questions.map(question => (
          <div key={question.key} className="flex flex-wrap items-center gap-2">
            <Input
              value={question.label}
              placeholder={translations.questionLabel}
              className="min-w-56 flex-1"
              onChange={label => updateQuestion(question.key, { label })}
            />

            <Combobox<EventQuestionType>
              mode="single"
              value={question.type}
              items={typeItems}
              noSearch
              className="w-40"
              onChange={type =>
                updateQuestion(question.key, { type: type ?? 'YES_NO' })
              }
            />

            <label className="flex items-center gap-2 text-sm text-stone-600">
              <Checkbox
                value={question.required}
                onChange={checked =>
                  updateQuestion(question.key, { required: checked === true })
                }
              />
              {translations.required}
            </label>

            <button
              type="button"
              className="text-stone-400 hover:text-stone-600"
              title={translations.remove}
              onClick={() =>
                update({
                  questions: values.questions.filter(
                    other => other.key !== question.key
                  ),
                })
              }
            >
              <FAIcon icon="xmark" />
            </button>
          </div>
        ))}

        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addQuestion(newQuestion())}
          >
            <FAIcon icon="plus" />
            {translations.addQuestion}
          </Button>

          {!hasPreset(translations.presetCompanion) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                addQuestion(newQuestion(translations.presetCompanion))
              }
            >
              {translations.presetCompanion}
            </Button>
          )}

          {!hasPreset(translations.presetOvernight) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                addQuestion(newQuestion(translations.presetOvernight))
              }
            >
              {translations.presetOvernight}
            </Button>
          )}
        </div>
      </div>

      {!!error && (
        <p className="m-0 text-sm text-red-600">{errorText[error]}</p>
      )}
    </div>
  )
}
